import { COGNITO_CONFIG } from '../../Utils/constants';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import { clearAllNlSearchRecentQueries } from '../../Map/utils/nlSearchRecentQueries';
import { pickCognitoIdToken } from '../utils/cognitoBearerToken';
import { computeClockDriftMs, msUntilTokenRefresh, shouldRefreshToken } from '../utils/cognitoJwt';

/** localStorage: `{ idToken, accessToken, refreshToken, clockDriftMs }`. */
export const COGNITO_TOKENS_STORAGE_KEY = 'cognito_tokens';

/**
 * `window` event: el refresh token murió (vencido o revocado) y la sesión se
 * cerró sola. `AuthContext` lo escucha para pasar a deslogueado con toast.
 */
export const AUTH_SESSION_EXPIRED_EVENT = 'terrio:auth-session-expired';

/** `window` event: se guardaron tokens nuevos en esta pestaña (login o refresh). */
export const AUTH_TOKENS_UPDATED_EVENT = 'terrio:auth-tokens-updated';

const REFRESH_LOCK_NAME = 'terrio-cognito-token-refresh';
/** Si Cognito no responde, el refresh no puede colgar las requests. */
const REFRESH_TIMEOUT_MS = 10000;
/**
 * Un 401 con un token recién renovado no dispara otro refresh hasta pasado
 * este tiempo: evita pedirle a Cognito en cada 401 de negocio.
 */
const MIN_FORCED_REFRESH_INTERVAL_MS = 30000;
/** Errores que significan refresh token muerto (vencido, revocado, usuario borrado). */
const DEAD_REFRESH_ERROR_TYPES = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'invalid_grant',
]);

// Función auxiliar para parsear JWT
const parseJWT = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
};

// Función para obtener el endpoint de Cognito
const getCognitoEndpoint = () => {
  return `https://cognito-idp.${COGNITO_CONFIG.region}.amazonaws.com/`;
};

/**
 * Challenge FORCE_CHANGE_PASSWORD (AdminCreateUser + temporal).
 * Hay que reusar el mismo CognitoUser / Session en completeNewPassword.
 * @type {{ cognitoUser: import('amazon-cognito-identity-js').CognitoUser, userAttributes: Record<string, string> } | null}
 */
let pendingNewPasswordChallenge = null;

const clearPendingNewPasswordChallenge = () => {
  pendingNewPasswordChallenge = null;
};

const dispatchAuthEvent = (name) => {
  try {
    window.dispatchEvent(new Event(name));
  } catch {
    // sin window (tests de node): nada que avisar
  }
};

/** @returns {{ idToken?: string, accessToken?: string, refreshToken?: string, clockDriftMs?: number }|null} */
const readStoredTokens = () => {
  try {
    const raw = localStorage.getItem(COGNITO_TOKENS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * `cognito_tokens` existe pero no se puede leer (JSON roto, no es un objeto).
 * Sin id ni refresh token legibles la sesión es irrecuperable: se trata como
 * vencida para no dejar la UI "logueada" mandando requests sin Bearer.
 */
const hasUnreadableStoredTokens = () => {
  try {
    return !!localStorage.getItem(COGNITO_TOKENS_STORAGE_KEY) && !readStoredTokens();
  } catch {
    return false;
  }
};

/**
 * Guarda tokens recién emitidos por Cognito (login SDK, Google/hosted UI o
 * refresh). Calcula el drift de reloj contra el `iat` del id token.
 *
 * @param {{ idToken: string, accessToken: string, refreshToken?: string }} tokens
 * @returns {{ tokens: object, userInfo: object }}
 */
export const persistCognitoTokens = ({ idToken, accessToken, refreshToken }) => {
  const tokens = {
    idToken,
    accessToken,
    refreshToken,
    clockDriftMs: computeClockDriftMs(idToken),
  };

  localStorage.setItem(COGNITO_TOKENS_STORAGE_KEY, JSON.stringify(tokens));
  localStorage.setItem('isLoggedIn', 'true');

  const userInfo = parseJWT(idToken);
  localStorage.setItem('user_info', JSON.stringify(userInfo));

  dispatchAuthEvent(AUTH_TOKENS_UPDATED_EVENT);
  return { tokens, userInfo };
};

/**
 * Persiste tokens de una sesión Cognito exitosa (login o challenge).
 * @param {import('amazon-cognito-identity-js').CognitoUserSession} session
 */
const persistAuthSession = (session) => persistCognitoTokens({
  idToken: session.getIdToken().getJwtToken(),
  accessToken: session.getAccessToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
});

/**
 * Atributos que Cognito no acepta de vuelta en RespondToAuthChallenge.
 * @param {Record<string, string>} userAttributes
 */
const sanitizeChallengeUserAttributes = (userAttributes = {}) => {
  const attrs = { ...userAttributes };
  delete attrs.email_verified;
  delete attrs.phone_number_verified;
  delete attrs.email;
  delete attrs.phone_number;
  return attrs;
};

const mapCompleteNewPasswordError = (error) => {
  switch (error?.code) {
    case 'InvalidPasswordException':
      return 'La contraseña no cumple los requisitos (mínimo 8 caracteres, mayúsculas, minúsculas, números y símbolos)';
    case 'InvalidParameterException':
      return error?.message || 'La contraseña no es válida';
    case 'NotAuthorizedException':
      return 'La sesión expiró. Volvé a iniciar sesión con la contraseña temporal.';
    default:
      return error?.message || 'No pudimos actualizar la contraseña. Intentá de nuevo.';
  }
};

// ✅ LOGIN - Iniciar sesión con email y contraseña
export const signIn = async (email, password) => {
  clearPendingNewPasswordChallenge();

  const poolData = {
    UserPoolId: COGNITO_CONFIG.userPoolId,
    ClientId: COGNITO_CONFIG.clientId,
  };

  const userPool = new CognitoUserPool(poolData);
  const authenticationDetails = new AuthenticationDetails({
    Username: email,
    Password: password,
  });

  const cognitoUser = new CognitoUser({
    Username: email,
    Pool: userPool,
  });

  try {
    const result = await new Promise((resolve) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          const { tokens, userInfo } = persistAuthSession(session);
          resolve({
            success: true,
            userInfo,
            tokens,
          });
        },
        onFailure: (error) => {
          let errorMessage = 'Error al iniciar sesión';

          switch (error?.code) {
            case 'UserNotConfirmedException':
              errorMessage = 'Tu cuenta no ha sido verificada. Revisá tu email.';
              break;
            case 'NotAuthorizedException':
              errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña.';
              break;
            case 'UserNotFoundException':
              errorMessage = 'No encontramos una cuenta con ese email y contraseña. Si te registraste con Google, usá el botón "Continuar con Google".';
              break;
            default:
              errorMessage = error?.message || 'Error al iniciar sesión';
          }

          resolve({ success: false, error: errorMessage });
        },
        newPasswordRequired: (userAttributes) => {
          pendingNewPasswordChallenge = {
            cognitoUser,
            userAttributes: sanitizeChallengeUserAttributes(userAttributes),
          };
          resolve({
            success: false,
            challenge: 'NEW_PASSWORD_REQUIRED',
          });
        },
      });
    });

    return result;
  } catch (error) {
    console.error('Error en signIn:', error);
    clearPendingNewPasswordChallenge();
    return {
      success: false,
      error: error?.message || 'Error de conexión',
    };
  }
};

/**
 * Completa el challenge NEW_PASSWORD_REQUIRED tras AdminCreateUser + temporal.
 * Debe llamarse después de un signIn que devolvió challenge: 'NEW_PASSWORD_REQUIRED'.
 * @param {string} newPassword
 */
export const completeNewPassword = async (newPassword) => {
  const pending = pendingNewPasswordChallenge;
  if (!pending?.cognitoUser) {
    return {
      success: false,
      error: 'La sesión expiró. Volvé a iniciar sesión con la contraseña temporal.',
    };
  }

  const { cognitoUser, userAttributes } = pending;

  try {
    const result = await new Promise((resolve) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (session) => {
          clearPendingNewPasswordChallenge();
          const { tokens, userInfo } = persistAuthSession(session);
          resolve({
            success: true,
            userInfo,
            tokens,
          });
        },
        onFailure: (error) => {
          resolve({
            success: false,
            error: mapCompleteNewPasswordError(error),
          });
        },
      });
    });

    return result;
  } catch (error) {
    console.error('Error en completeNewPassword:', error);
    return {
      success: false,
      error: error?.message || 'Error de conexión',
    };
  }
};

/** Cancela un challenge NEW_PASSWORD_REQUIRED pendiente (volver al login). */
export const cancelNewPasswordChallenge = () => {
  clearPendingNewPasswordChallenge();
};

// ✅ REGISTRO - Crear nueva cuenta
export const signUp = async (email, password, name = '') => {
  try {
    const params = {
      ClientId: COGNITO_CONFIG.clientId,
      Username: email,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email }
      ],
    };

    if (name) {
      params.UserAttributes.push({ Name: 'name', Value: name });
    }

    const response = await fetch(getCognitoEndpoint(), {
      method: 'POST',
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        userConfirmed: result.UserConfirmed,
        userSub: result.UserSub,
      };
    } else {
      let errorMessage = 'Error al registrar usuario';
      
      if (result.__type) {
        switch (result.__type) {
          case 'InvalidPasswordException':
            errorMessage = 'La contraseña no cumple los requisitos (mínimo 8 caracteres, mayúsculas, minúsculas, números y símbolos)';
            break;
          case 'UsernameExistsException':
            errorMessage = 'Este email ya está registrado';
            break;
          default:
            errorMessage = result.message || 'Error al registrar usuario';
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error) {
    console.error('Error en signUp:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión',
    };
  }
};

// ✅ VERIFICAR CÓDIGO - Confirmar email
export const confirmSignUp = async (email, code) => {
  try {
    const params = {
      ClientId: COGNITO_CONFIG.clientId,
      Username: email,
      ConfirmationCode: code,
    };

    const response = await fetch(getCognitoEndpoint(), {
      method: 'POST',
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp',
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true };
    } else {
      return {
        success: false,
        error: result.message || 'Código inválido',
      };
    }
  } catch (error) {
    console.error('Error en confirmSignUp:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión',
    };
  }
};

// ✅ RECUPERAR CONTRASEÑA - Enviar código
export const forgotPassword = async (email) => {
  try {
    const params = {
      ClientId: COGNITO_CONFIG.clientId,
      Username: email,
    };

    const response = await fetch(getCognitoEndpoint(), {
      method: 'POST',
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword',
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true };
    } else {
      return {
        success: false,
        error: result.message || 'Error al enviar código',
      };
    }
  } catch (error) {
    console.error('Error en forgotPassword:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión',
    };
  }
};

// ✅ CAMBIAR CONTRASEÑA - Con código de recuperación
export const confirmForgotPassword = async (email, code, newPassword) => {
  try {
    const params = {
      ClientId: COGNITO_CONFIG.clientId,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    };

    const response = await fetch(getCognitoEndpoint(), {
      method: 'POST',
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true };
    } else {
      return {
        success: false,
        error: result.message || 'Error al cambiar contraseña',
      };
    }
  } catch (error) {
    console.error('Error en confirmForgotPassword:', error);
    return {
      success: false,
      error: error.message || 'Error de conexión',
    };
  }
};

/**
 * Borra las claves que cachea `amazon-cognito-identity-js` en `authenticateUser`
 * (`CognitoIdentityServiceProvider.<clientId>.*`, incluido su refresh token).
 */
const clearCognitoSdkStorage = () => {
  try {
    const prefix = `CognitoIdentityServiceProvider.${COGNITO_CONFIG.clientId}.`;
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

// ✅ CERRAR SESIÓN
export const signOut = () => {
  try {
    const raw = localStorage.getItem('user_info');
    const info = raw ? JSON.parse(raw) : null;
    const userKey = info?.sub || info?.email || info?.username;
    if (userKey) {
      clearAllNlSearchRecentQueries(userKey);
    }
  } catch {
    // ignore
  }
  clearPendingNewPasswordChallenge();
  localStorage.removeItem(COGNITO_TOKENS_STORAGE_KEY);
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('user_info');
  clearCognitoSdkStorage();
};

// ─────────────────────────────────────────────────────────────────────────────
// Renovación de tokens (docs/auth/cognito-session.md)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Único punto que habla con Cognito para renovar. Hoy: `InitiateAuth` con
 * `REFRESH_TOKEN_AUTH` (mismo endpoint que el login del SDK; sirve para
 * email/contraseña y Google). Pasar a `POST /oauth2/token`
 * (`grant_type=refresh_token`) es cambiar solo esta función.
 *
 * @param {string} refreshToken
 * @returns {Promise<{ ok: true, tokens: { idToken: string, accessToken: string, refreshToken: string } } | { ok: false, dead: boolean, error: string }>}
 */
const requestTokenRefresh = async (refreshToken) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const response = await fetch(getCognitoEndpoint(), {
      method: 'POST',
      headers: {
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        'Content-Type': 'application/x-amz-json-1.1',
      },
      body: JSON.stringify({
        ClientId: COGNITO_CONFIG.clientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
      signal: controller.signal,
    });
    const result = await response.json().catch(() => ({}));
    const auth = result?.AuthenticationResult;
    if (response.ok && auth?.IdToken && auth?.AccessToken) {
      return {
        ok: true,
        tokens: {
          idToken: auth.IdToken,
          accessToken: auth.AccessToken,
          // Rotación apagada: Cognito no devuelve uno nuevo y se conserva el actual.
          refreshToken: auth.RefreshToken || refreshToken,
        },
      };
    }
    const errorType = String(result?.__type || result?.error || '').split('#').pop();
    return {
      ok: false,
      dead: DEAD_REFRESH_ERROR_TYPES.has(errorType),
      error: errorType || `HTTP ${response.status}`,
    };
  } catch (error) {
    return {
      ok: false,
      dead: false,
      error: error?.name === 'AbortError' ? 'timeout' : (error?.message || 'network'),
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

/** Cierra la sesión local porque el refresh token ya no sirve, y avisa. */
const endExpiredSession = () => {
  // Sin clave (ya se cerró, p. ej. otra request): no repetir el aviso.
  if (!readStoredTokens() && !hasUnreadableStoredTokens()) return;
  signOut();
  dispatchAuthEvent(AUTH_SESSION_EXPIRED_EVENT);
};

/** Serializa el refresh entre pestañas (Web Locks) cuando el browser lo soporta. */
const withRefreshLock = (task) => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (locks && typeof locks.request === 'function') {
    return locks.request(REFRESH_LOCK_NAME, task);
  }
  return task();
};

let refreshInFlight = null;
let lastRefreshAt = 0;

const NO_SESSION = { idToken: null, accessToken: null };

const runTokenRefresh = async (staleIdToken) => {
  // Se relee después de tomar el lock: otra pestaña pudo haber renovado.
  const stored = readStoredTokens();
  if (!stored) {
    if (hasUnreadableStoredTokens()) {
      endExpiredSession();
      return { status: 'dead', ...NO_SESSION };
    }
    return { status: 'no-session', ...NO_SESSION };
  }

  const current = {
    idToken: pickCognitoIdToken(stored),
    accessToken: stored.accessToken || null,
  };

  if (staleIdToken) {
    if (current.idToken && current.idToken !== staleIdToken) {
      return { status: 'unchanged', ...current };
    }
    if (Date.now() - lastRefreshAt < MIN_FORCED_REFRESH_INTERVAL_MS) {
      return { status: 'unchanged', ...current };
    }
  } else if (
    current.idToken
    && !shouldRefreshToken(current.idToken, { driftMs: stored.clockDriftMs })
  ) {
    return { status: 'unchanged', ...current };
  }

  if (!stored.refreshToken) {
    endExpiredSession();
    return { status: 'dead', ...NO_SESSION };
  }

  const result = await requestTokenRefresh(stored.refreshToken);

  if (result.ok) {
    // Logout (en esta u otra pestaña) mientras volaba el refresh: no revivir.
    if (!readStoredTokens()) return { status: 'no-session', ...NO_SESSION };
    persistCognitoTokens(result.tokens);
    lastRefreshAt = Date.now();
    return {
      status: 'refreshed',
      idToken: pickCognitoIdToken(result.tokens),
      accessToken: result.tokens.accessToken,
    };
  }

  if (result.dead) {
    endExpiredSession();
    return { status: 'dead', ...NO_SESSION };
  }

  console.error('Auth: no se pudo renovar el token de Cognito', result.error);
  return { status: 'failed', ...current };
};

/**
 * Renueva los tokens con el refresh token. Una sola renovación en vuelo por
 * pestaña, y serializada entre pestañas con `navigator.locks`.
 *
 * Sin `staleIdToken` (proactivo): solo renueva si el id token vence dentro del
 * margen. Con `staleIdToken` (tras un 401): renueva salvo que el token guardado
 * ya sea otro, o que se haya renovado hace menos de 30 s.
 *
 * - `refreshed` / `unchanged`: tokens válidos en `idToken` / `accessToken`.
 * - `dead`: refresh token vencido o revocado (o `cognito_tokens` ilegible); la
 *   sesión se cerró y se emitió `AUTH_SESSION_EXPIRED_EVENT`.
 * - `failed`: red / timeout / throttling. La sesión sigue; vuelven los tokens actuales.
 * - `no-session`: no hay sesión.
 *
 * @param {{ staleIdToken?: string|null }} [options]
 * @returns {Promise<{ status: 'refreshed'|'unchanged'|'dead'|'failed'|'no-session', idToken: string|null, accessToken: string|null }>}
 */
export const refreshAuthSession = ({ staleIdToken = null } = {}) => {
  if (refreshInFlight) {
    if (!staleIdToken) return refreshInFlight;
    // Un refresh proactivo en vuelo puede terminar sin renovar: el 401 pide el suyo.
    return refreshInFlight.then((result) => (
      result.status === 'unchanged' && result.idToken === staleIdToken
        ? refreshAuthSession({ staleIdToken })
        : result
    ));
  }

  refreshInFlight = Promise.resolve()
    .then(() => withRefreshLock(() => runTokenRefresh(staleIdToken)))
    .catch((error) => {
      console.error('Auth: refresh de token falló', error);
      const stored = readStoredTokens();
      return stored
        ? { status: 'failed', idToken: pickCognitoIdToken(stored), accessToken: stored.accessToken || null }
        : { status: 'no-session', ...NO_SESSION };
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

/**
 * Milisegundos hasta que conviene renovar el id token guardado, o `null` si no
 * hay sesión. Lo usa el scheduler proactivo de `AuthProvider`. Con
 * `cognito_tokens` ilegible devuelve 0: el scheduler corre ya y cierra la sesión.
 *
 * @returns {number|null}
 */
export const getMsUntilTokenRefresh = () => {
  const stored = readStoredTokens();
  if (!stored) return hasUnreadableStoredTokens() ? 0 : null;
  const idToken = pickCognitoIdToken(stored);
  if (!idToken) return 0;
  return Math.max(0, msUntilTokenRefresh(idToken, { driftMs: stored.clockDriftMs }));
};

/**
 * Id token válido para `Authorization: Bearer`. Si vence en menos de 5 min,
 * renueva antes. Sin sesión, con el refresh token muerto o con `cognito_tokens`
 * ilegible (cierra la sesión y avisa) devuelve `null`
 * (la request sale anónima). Si el refresh falla por red, devuelve el actual.
 *
 * @returns {Promise<string|null>}
 */
export const getValidIdToken = async () => {
  const stored = readStoredTokens();
  if (!stored) {
    // Ilegible: cierra la sesión (una vez, vía refresh single-flight) y sale anónima.
    if (hasUnreadableStoredTokens()) await refreshAuthSession();
    return null;
  }
  const idToken = pickCognitoIdToken(stored);
  if (idToken && !shouldRefreshToken(idToken, { driftMs: stored.clockDriftMs })) {
    return idToken;
  }
  const result = await refreshAuthSession();
  if (result.status === 'dead' || result.status === 'no-session') return null;
  return result.idToken || idToken;
};

/**
 * Access token válido (feedback y reporte de errores). Misma política que
 * `getValidIdToken`.
 *
 * @returns {Promise<string|null>}
 */
export const getValidAccessToken = async () => {
  const stored = readStoredTokens();
  if (!stored) {
    if (hasUnreadableStoredTokens()) await refreshAuthSession();
    return null;
  }
  const accessToken = stored.accessToken || null;
  if (accessToken && !shouldRefreshToken(accessToken, { driftMs: stored.clockDriftMs })) {
    return accessToken;
  }
  const result = await refreshAuthSession();
  if (result.status === 'dead' || result.status === 'no-session') return null;
  return result.accessToken || accessToken;
};

/**
 * Bearer para el API. Siempre el **id token** (claim `email` para allowlist
 * restricted). El access token de Cognito suele no traer email: entonces
 * `getsharedwithmelists` / `getsharedlist` solo matchean filas `type: sub`.
 */
export const getCurrentToken = () => {
  try {
    const tokens = localStorage.getItem(COGNITO_TOKENS_STORAGE_KEY);
    if (!tokens) return null;
    return pickCognitoIdToken(JSON.parse(tokens));
  } catch {
    return null;
  }
};

// ✅ VERIFICAR SI ESTÁ AUTENTICADO
export const isAuthenticated = () => {
  const tokens = localStorage.getItem(COGNITO_TOKENS_STORAGE_KEY);
  return !!tokens;
};

// ✅ OBTENER INFORMACIÓN DEL USUARIO
export const getUserInfo = () => {
  try {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    return null;
  }
};